import React, { useState, useEffect /* , useRef */ } from 'react'; // useRef commented out: unused import
import { User as UserType } from '../types';
import { apiService } from '../services/api';
import Footer from './Footer';
import Header from './Header';
import { User, Mail, Lock } from 'lucide-react';
import authBackground from '../assets/auth-background.jpg';
import './Auth.css';

interface AuthProps {
  onLogin: (user: UserType, token: string) => void;
  onNavigate?: (path: string) => void;
  mode?: 'login' | 'register';
}

const Auth: React.FC<AuthProps> = ({ onLogin, onNavigate, mode }) => {
  const [isLogin, setIsLogin] = useState(mode !== 'register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.classList.add('auth-lock');

    return () => {
      document.body.classList.remove('auth-lock');
    };
  }, []);

  // Keep tab state in sync with mode prop (URL-driven)
  useEffect(() => {
    if (mode === 'register') {
      setIsLogin(false);
    } else if (mode === 'login') {
      setIsLogin(true);
    }
  }, [mode]);

  const handleSubmit = async (e: React.FormEvent) => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔧 handleSubmit FUNCTION CALLED:', new Date().toISOString());
    console.log('  Event:', e.type);
    console.log('  Calling preventDefault...');
    
    e.preventDefault();
    
    console.log('  preventDefault called');
    console.log('  Setting loading to true...');
    setLoading(true);
    console.log('  Clearing error...');
    setError('');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    // Enhanced debugging for mobile
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🔐 AUTH FORM SUBMITTED:', new Date().toISOString());
    console.log('  Mode:', isLogin ? 'login' : 'register');
    console.log('  Email:', email);
    console.log('  Has password:', !!password);
    console.log('  Password length:', password.length);
    console.log('  API URL:', process.env.REACT_APP_API_URL);
    console.log('  User Agent:', navigator.userAgent);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

    try {
      let data;
      
      if (isLogin) {
        console.log('📤 Calling apiService.login()...');
        const loginStartTime = Date.now();
        try {
          data = await apiService.login(email, password);
          const loginDuration = Date.now() - loginStartTime;
          console.log('✅ Login completed:', {
            duration: `${loginDuration}ms`,
            userId: data.user?.id,
            email: data.user?.email,
            onboardingCompleted: data.user?.onboardingCompleted,
            hasToken: !!data.token
          });
        } catch (loginError) {
          console.error('❌ Login failed:', loginError);
          throw loginError;
        }
      } else {
        console.log('📤 Calling apiService.register()...');
        const registerStartTime = Date.now();
        try {
          data = await apiService.register(email, password, name || undefined);
          const registerDuration = Date.now() - registerStartTime;
          console.log('✅ Registration completed:', {
            duration: `${registerDuration}ms`,
            userId: data.user?.id,
            email: data.user?.email,
            onboardingCompleted: data.user?.onboardingCompleted,
            hasToken: !!data.token
          });
        } catch (registerError) {
          console.error('❌ Registration failed:', registerError);
          throw registerError;
        }
      }

      // Token is now stored in HTTP-only cookie by backend
      // No need to store in localStorage anymore
      console.log('🍪 Using HTTP-only cookie authentication (token in cookie)');

      // Call parent callback
      console.log('📞 Calling onLogin callback with:', {
        userId: data.user?.id,
        email: data.user?.email,
        onboardingCompleted: data.user?.onboardingCompleted,
        hasToken: !!data.token
      });
      onLogin(data.user, data.token);
      console.log('✅ onLogin callback completed');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      console.error('❌ Auth error:', {
        message: errorMessage,
        error: err,
        status: (err as any)?.status,
        apiUrl: process.env.REACT_APP_API_URL
      });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" style={{ backgroundImage: `url(${authBackground})` }}>
      <Header onNavigate={onNavigate} />
        <div className="auth-container">
          <div className="auth-card">
          <div className="auth-header">
          </div>

          <div className="auth-tabs">
            <button
              className={`tab ${isLogin ? 'active' : ''}`}
              onClick={() => {
                if (onNavigate) {
                  onNavigate('/login');
                } else {
                  setIsLogin(true);
                }
              }}
            >
              Sign In
            </button>
            <button
              className={`tab ${!isLogin ? 'active' : ''}`}
              onClick={() => {
                if (onNavigate) {
                  onNavigate('/register');
                } else {
                  setIsLogin(false);
                }
              }}
            >
              Sign Up
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="auth-form"
            noValidate
          >
            {!isLogin && (
              <div className="form-group">
                <div className="input-with-icon">
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name"
                  />
                  {!name && <User className="field-icon" size={16} />}
                </div>
              </div>
            )}

            <div className="form-group">
              <div className="input-with-icon">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  required
                />
                {!email && <Mail className="field-icon" size={16} />}
              </div>
            </div>

            <div className="form-group">
              <div className="input-with-icon">
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  minLength={6}
                />
                {!password && <Lock className="field-icon" size={16} />}
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className="auth-button"
              disabled={loading}
            >
              {loading ? 'Loading...' : (isLogin ? 'Get Started' : 'Get Started')}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  if (onNavigate) {
                    onNavigate(isLogin ? '/register' : '/login');
                  } else {
                    setIsLogin(!isLogin);
                  }
                }}
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
      </div>
      {onNavigate && <Footer onNavigate={onNavigate} />}
    </div>
  );
};

export default Auth;
